import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useDatastore } from '../hooks/useDatastore';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { UserDetailsProps } from '../datastore/userDetails';

interface UserDetailsContextType {
  userDetails: UserDetailsProps | undefined;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const UserDetailsContext = createContext<UserDetailsContextType | undefined>(undefined);

export function UserDetailsProvider({ children }: { children: React.ReactNode }) {
  const store = useDatastore();
  const user = useCurrentUser();

  const [userDetails, setUserDetails] = useState<UserDetailsProps | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUserDetails = useCallback(async () => {
    // Guard: wait until everything is ready
    if (!store.isDatastoreReady || store.isSyncing || !store.userDetails || !user?.cognitoUser?.username) {
      return;
    }

    try {
      setIsLoading(true);

      const details = await store.userDetails.get(user.cognitoUser.username, user.cognitoUser);

      setUserDetails(details);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setIsLoading(false);
    }
  }, [store.isDatastoreReady, store.isSyncing, store.userDetails, user.cognitoUser]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const refetch = async () => {
    await fetchUserDetails();
  };

  return (
    <UserDetailsContext.Provider value={{ userDetails, isLoading, refetch }}>{children}</UserDetailsContext.Provider>
  );
}

export function useUserDetails() {
  const context = useContext(UserDetailsContext);

  if (!context) {
    throw new Error('useUserDetails must be used within a UserDetailsProvider');
  }

  return context;
}
